import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, API, errMsg, money } from "@/lib/api";

const Stat = ({ label, value, id }) => (
  <div className="bg-[#121215] p-6"><p className="eyebrow">{label}</p><p data-testid={`admin-stat-${id}`} className="font-display mt-2 text-4xl">{value}</p></div>
);

const DecideBtns = ({ onDecide, id }) => (
  <div className="flex gap-2">
    <button data-testid={`approve-${id}`} onClick={() => onDecide("approve")} className="btn-red rounded-full px-5 py-2 text-xs font-semibold">Approve</button>
    <button data-testid={`reject-${id}`} onClick={() => onDecide("reject")} className="rounded-full border border-white/20 px-5 py-2 text-xs hover:bg-white hover:text-black">Reject</button>
  </div>
);

function Verifications({ onChange }) {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/verifications").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const decide = async (id, decision) => {
    try { await api.post(`/admin/verifications/${id}`, { decision }); toast.success(`Verification ${decision}d`); load(); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!list.length) return <p className="py-10 text-zinc-500">No pending verifications.</p>;
  return list.map((v) => (
    <div key={v.id} data-testid={`verification-${v.id}`} className="card-lux mb-4 grid gap-5 p-5 md:grid-cols-[1fr_1fr_1.2fr]">
      <img src={`${API}/files/${v.id_file}`} alt="ID" className="aspect-[4/3] w-full bg-black object-contain" />
      <img src={`${API}/files/${v.selfie_file}`} alt="Selfie" className="aspect-[4/3] w-full bg-black object-contain" />
      <div className="flex flex-col justify-between gap-4">
        <div><p className="font-display text-2xl">{v.user?.name}</p><p className="text-sm text-zinc-400">{v.user?.email}</p><p className="text-sm text-zinc-400">DOB {v.user?.dob} · age {v.user?.age}</p></div>
        <DecideBtns id={v.id} onDecide={(d) => decide(v.id, d)} />
      </div>
    </div>
  ));
}

function Payouts({ onChange }) {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/payouts").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const decide = async (id, decision) => {
    try { await api.post(`/admin/payouts/${id}`, { decision }); toast.success(decision === "approve" ? "Marked as paid" : "Rejected and refunded"); load(); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!list.length) return <p className="py-10 text-zinc-500">No pending payouts.</p>;
  return list.map((p) => (
    <div key={p.id} data-testid={`payout-${p.id}`} className="card-lux mb-3 flex flex-wrap items-center justify-between gap-4 p-5">
      <div><p className="font-display text-3xl">{money(p.amount_cents)}</p><p className="text-sm text-zinc-400">{p.user?.name} · {p.user?.email}</p><p className="text-xs text-zinc-500">{p.method}: {p.details}</p></div>
      <DecideBtns id={p.id} onDecide={(d) => decide(p.id, d)} />
    </div>
  ));
}

function Users() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/users").then((r) => setList(r.data)); }, []);
  return (
    <div className="overflow-x-auto" data-testid="admin-users-table">
      <table className="w-full text-left text-sm">
        <thead className="eyebrow"><tr>{["Name", "Email", "Plan", "ID", "Wallet", "Tokens"].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
        <tbody>{list.map((u) => (
          <tr key={u.id} className="border-t border-white/5"><td className="p-3">{u.name}</td><td className="p-3 text-zinc-400">{u.email}</td><td className="p-3 uppercase">{u.membership}</td><td className="p-3">{u.id_status}</td><td className="p-3">{money(u.wallet_cents)}</td><td className="p-3">{u.tokens}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export default function Admin() {
  const [s, setS] = useState(null);
  const loadStats = () => api.get("/admin/stats").then((r) => setS(r.data));
  useEffect(() => { loadStats(); }, []);
  return (
    <main className="mx-auto max-w-7xl px-5 py-12 lg:px-8" data-testid="admin-page">
      <p className="eyebrow mb-3">Control room</p>
      <h1 className="font-display mb-10 text-5xl">Admin</h1>
      {s && (
        <div className="mb-12 grid grid-cols-2 gap-px bg-white/10 lg:grid-cols-4">
          <Stat id="users" label="Members" value={s.users} />
          <Stat id="vips" label="Active VIPs" value={s.vips} />
          <Stat id="revenue" label="Stripe revenue" value={money(s.stripe_revenue_cents)} />
          <Stat id="commission" label="10% content fees" value={money(s.commission_cents)} />
        </div>
      )}
      <Tabs defaultValue="verify">
        <TabsList className="mb-6 bg-zinc-900">
          <TabsTrigger data-testid="admin-tab-verify" value="verify">ID queue {s ? `(${s.pending_verifications})` : ""}</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-payouts" value="payouts">Payouts {s ? `(${s.pending_payouts})` : ""}</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-users" value="users">Users</TabsTrigger>
        </TabsList>
        <TabsContent value="verify"><Verifications onChange={loadStats} /></TabsContent>
        <TabsContent value="payouts"><Payouts onChange={loadStats} /></TabsContent>
        <TabsContent value="users"><Users /></TabsContent>
      </Tabs>
    </main>
  );
}
